import React from 'react';
import type { HelpTopic } from './types';

export const helpTopics: HelpTopic[] = [
  // ═══════════════════════════════════════════════════════════════════
  // QUICK START
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'qs-commander',
    slug: 'incident-commander-guide',
    title: 'Incident Commander Guide',
    summary: 'Quick-start guide for Incident Commanders managing an evacuation event from start to close.',
    category: 'Quick Start',
    audience: ['Incident Commander'],
    keywords: ['commander', 'guide', 'quick start', 'getting started', 'lead', 'manage'],
    relatedTopicSlugs: ['create-incident', 'incident-dashboard', 'close-incident', 'incident-team'],
    sections: [
      {
        id: 'overview',
        title: 'Your Role',
        body: (
          <>
            <p>As an Incident Commander, you are responsible for creating incidents, monitoring accountability progress, coordinating headcounters, and closing the incident once all personnel are accounted for.</p>
          </>
        ),
      },
      {
        id: 'workflow',
        title: 'Typical Workflow',
        body: (
          <ol>
            <li><strong>Create Incident</strong> — Go to Incidents and click "New Incident". Select the incident type, choose a location, and click "Start Incident".</li>
            <li><strong>Monitor Dashboard</strong> — The Incident Dashboard shows live KPI cards, accountability progress, and alert cards for people needing attention.</li>
            <li><strong>Coordinate Headcounters</strong> — Use the Team page or Team Drawer to see who is assigned. Headcounters will mark people as Accounted or Not Accounted.</li>
            <li><strong>Review Progress</strong> — Use Review &amp; Submit to record checkpoints. Checkpoints save a snapshot but do not close the incident.</li>
            <li><strong>Close Incident</strong> — When accountability is complete, click "Close Incident" on the Dashboard. Review the summary, acknowledge any remaining unresolved people, and close.</li>
          </ol>
        ),
      },
      {
        id: 'tips',
        title: 'Tips',
        body: (
          <ul>
            <li>Keep the Dashboard open — it auto-refreshes and receives real-time updates.</li>
            <li>Use "Export Report" to download a CSV summary at any time.</li>
            <li>If you see unsynced changes, wait for the pending sync to complete before closing.</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: 'qs-headcounter',
    slug: 'headcounter-guide',
    title: 'Headcounter Guide',
    summary: 'Quick-start guide for headcounters marking people as Accounted or Not Accounted during an incident.',
    category: 'Quick Start',
    audience: ['Headcounter'],
    keywords: ['headcounter', 'guide', 'quick start', 'mark', 'accounted', 'accountability'],
    relatedTopicSlugs: ['headcount', 'roster-views', 'roster-scopes-filters', 'not-accounted'],
    sections: [
      {
        id: 'overview',
        title: 'Your Role',
        body: (
          <p>As a Headcounter, you are responsible for physically locating people and marking their accountability status in the app. You work through a roster of people assigned to your location or view.</p>
        ),
      },
      {
        id: 'workflow',
        title: 'Typical Workflow',
        body: (
          <ol>
            <li><strong>Open the Incident</strong> — From the Incident List, click the active incident card.</li>
            <li><strong>Choose a Roster View</strong> — Select "Full Roster", a specific floor, practice, or person type to narrow your list.</li>
            <li><strong>Mark People</strong> — Tap "Accounted" or "Not Accounted" for each person. Changes are saved automatically every 5 seconds.</li>
            <li><strong>Add Unlisted People</strong> — If someone is present but not on the roster, click "Add Person" to add them manually.</li>
            <li><strong>Review Progress</strong> — Click "Review Progress" to see a summary and record a checkpoint.</li>
          </ol>
        ),
      },
      {
        id: 'tips',
        title: 'Tips',
        body: (
          <ul>
            <li>Use search to find a specific person quickly.</li>
            <li>Watch the pending sync indicator — do not navigate away while changes are syncing.</li>
            <li>If you go offline, changes are preserved and will sync when connectivity returns.</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: 'qs-reports',
    slug: 'reports-guide',
    title: 'Reports Guide',
    summary: 'Quick-start guide for viewing incident reports and exporting data.',
    category: 'Quick Start',
    audience: ['Incident Commander', 'Admin', 'Viewer'],
    keywords: ['reports', 'guide', 'export', 'csv', 'download'],
    relatedTopicSlugs: ['reports-exports', 'incident-dashboard'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Reports page lets you browse all incidents (active and closed), search by name, and open individual incident reports with summary metrics.</p>
        ),
      },
      {
        id: 'workflow',
        title: 'How to Use Reports',
        body: (
          <ol>
            <li>Navigate to <strong>Reports</strong> in the left sidebar.</li>
            <li>Use status filter chips (Active, Closed, All) to narrow results.</li>
            <li>Search for an incident by name.</li>
            <li>Click an incident to view its report page with summary metrics.</li>
            <li>Use "Export Report" on the Dashboard or "Export List" on the Unaccounted page to download CSV files.</li>
          </ol>
        ),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // CORE WORKFLOW
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'cw-getting-started',
    slug: 'getting-started',
    title: 'Getting Started',
    summary: 'Overview of the EEAS app shell, navigation, and main sections.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['getting started', 'navigation', 'sidebar', 'header', 'breadcrumbs', 'app shell', 'layout', 'sign out', 'menu'],
    relatedTopicSlugs: ['incident-list', 'roles-permissions'],
    sections: [
      {
        id: 'app-shell',
        title: 'App Shell',
        body: (
          <p>EEAS uses a fixed top header with the Exponent logo and your user identity, plus a collapsible left sidebar for navigation. On mobile devices, the sidebar becomes a slide-out drawer.</p>
        ),
      },
      {
        id: 'navigation',
        title: 'Left Navigation',
        body: (
          <>
            <p>The sidebar contains these top-level links:</p>
            <ul>
              <li><strong>Incidents</strong> — The main landing page showing all active and past incidents. When you enter an incident, sub-items appear: Dashboard, Roster Views, Headcount, Not Accounted, Team, and Facility Map.</li>
              <li><strong>Reports</strong> — Cross-incident reporting and search.</li>
              <li><strong>Settings</strong> — Application configuration (Locations &amp; Assembly Areas).</li>
              <li><strong>Help</strong> — This Help Center.</li>
            </ul>
          </>
        ),
      },
      {
        id: 'breadcrumbs',
        title: 'Breadcrumbs',
        body: (
          <p>Most pages display breadcrumb navigation near the top, showing your current location in the app hierarchy. Click any breadcrumb link to navigate back to that level.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-incident-list',
    slug: 'incident-list',
    title: 'Incident List',
    summary: 'View, filter, and manage active and past incidents from the landing page.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['incident list', 'incidents', 'active', 'closed', 'past', 'landing', 'home', 'filter', 'location filter'],
    relatedRoutes: ['/'],
    relatedTopicSlugs: ['create-incident', 'incident-dashboard'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Incident List is the first page you see after signing in. It shows all incidents separated into Active Incidents (highlighted in red) and Past Incidents (gray). The page auto-refreshes every 15 seconds.</p>
        ),
      },
      {
        id: 'incident-cards',
        title: 'Incident Cards',
        body: (
          <p>Each incident card shows the incident name, type icon, location, accountability progress bar, key stats (total, accounted, not accounted), team summary, and a status chip (Active or Closed).</p>
        ),
      },
      {
        id: 'location-filter',
        title: 'Location Filter',
        body: (
          <p>Use the location dropdown at the top to filter incidents by location. The filter is de-duplicated and alphabetically sorted. All counts and status chips update when you change the filter.</p>
        ),
      },
      {
        id: 'new-incident',
        title: 'Creating a New Incident',
        body: (
          <>
            <p>Click the <strong>"New Incident"</strong> button to start a new evacuation event. This button only appears for users with the <em>Incident.Create</em> permission.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'cw-create-incident',
    slug: 'create-incident',
    title: 'Create Incident',
    summary: 'How to create a new evacuation incident with type, location, and notes.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Admin'],
    keywords: ['create', 'new incident', 'start incident', 'drill', 'fire', 'weather', 'medical', 'security', 'hazmat', 'roster', 'data warehouse'],
    relatedRoutes: ['/incidents/new'],
    relatedTopicSlugs: ['incident-list', 'incident-dashboard', 'roster-views', 'roles-permissions'],
    sections: [
      {
        id: 'steps',
        title: 'Steps to Create an Incident',
        body: (
          <ol>
            <li>Click <strong>"New Incident"</strong> on the Incident List page.</li>
            <li>Select an incident type from the 6-card grid: Drill, Fire, Weather, Medical, Security, or Hazmat.</li>
            <li>Choose a location — the field may be pre-filled from your security context.</li>
            <li>The incident name is auto-generated based on type and date/time. Edit it only if needed.</li>
            <li>Optionally add notes (up to 250 characters).</li>
            <li>Click <strong>"Start Incident"</strong>. A progress dialog shows while the roster is generated from the Data Warehouse.</li>
            <li>You are automatically navigated to Roster Views to begin headcount.</li>
          </ol>
        ),
      },
      {
        id: 'permissions',
        title: 'Permissions',
        body: (
          <p>The "New Incident" button only appears for users with the <em>Incident.Create</em> permission. If you do not see the button, contact your administrator.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-incident-dashboard',
    slug: 'incident-dashboard',
    title: 'Incident Dashboard',
    summary: 'Monitor accountability progress, view KPIs, manage team, and export reports.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['dashboard', 'kpi', 'progress', 'accountability', 'live', 'elapsed', 'timer', 'export', 'report', 'team', 'close'],
    relatedRoutes: ['/incidents/:incidentId/incidentdashboard'],
    relatedTopicSlugs: ['headcount', 'not-accounted', 'close-incident', 'incident-team', 'reports-exports'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Incident Dashboard is your operational command center. It shows real-time accountability status with live updates via SignalR and fallback polling.</p>
        ),
      },
      {
        id: 'kpi-cards',
        title: 'KPI Cards',
        body: (
          <p>Five KPI cards are displayed at the top: <strong>Total</strong>, <strong>Accounted</strong>, <strong>Not Accounted</strong>, <strong>Not Checked</strong>, and <strong>Unsynced</strong>. These update in real time as headcounters mark people.</p>
        ),
      },
      {
        id: 'progress-bar',
        title: 'Accountability Progress',
        body: (
          <p>The progress bar shows the percentage of people accounted for. Color coding: green when near complete, amber for in-progress, red when many people need attention.</p>
        ),
      },
      {
        id: 'panels',
        title: 'Data Panels',
        body: (
          <ul>
            <li><strong>Next Action Panel</strong> — Contextual guidance for the current workflow step.</li>
            <li><strong>Accountability by View</strong> — Breakdown of progress by roster view.</li>
            <li><strong>Recent Activity</strong> — Paginated feed of recent status changes.</li>
            <li><strong>Headcounter Activity</strong> — Summary of updates by each headcounter.</li>
          </ul>
        ),
      },
      {
        id: 'team',
        title: 'Team Summary',
        body: (
          <p>The team summary strip shows assigned team members. Click it to open the Team Drawer with full contact details and role assignments.</p>
        ),
      },
      {
        id: 'actions',
        title: 'Quick Actions',
        body: (
          <ul>
            <li><strong>Start/Continue Headcount</strong> — Navigate to the Headcount page.</li>
            <li><strong>Export Headcount Report</strong> — Download a full roster CSV.</li>
            <li><strong>Export Report</strong> (header button) — Download a summary CSV.</li>
            <li><strong>Close Incident</strong> — Open the close incident dialog.</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: 'cw-edit-incident',
    slug: 'edit-incident',
    title: 'Edit Incident',
    summary: 'Edit incident metadata such as type, name, and notes.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Admin'],
    keywords: ['edit', 'update', 'rename', 'incident type', 'notes', 'metadata'],
    relatedRoutes: ['/incidents/:incidentId/edit'],
    relatedTopicSlugs: ['incident-dashboard', 'create-incident', 'roles-permissions'],
    sections: [
      {
        id: 'what-can-edit',
        title: 'What You Can Edit',
        body: (
          <ul>
            <li>Incident type (Drill, Fire, Weather, etc.)</li>
            <li>Incident name</li>
            <li>Notes</li>
          </ul>
        ),
      },
      {
        id: 'what-cannot-edit',
        title: 'What Cannot Be Changed',
        body: (
          <p>Location, incident status, roster data, and headcount status cannot be changed through the Edit page. These are managed through the Dashboard and Headcount workflows.</p>
        ),
      },
      {
        id: 'behavior',
        title: 'Behavior',
        body: (
          <ul>
            <li>Both active and closed incidents can be edited by authorized users.</li>
            <li>The page tracks dirty state — if you navigate away with unsaved changes, a discard confirmation dialog appears.</li>
            <li>Optimistic concurrency handling prevents conflicts if another user edits the same incident simultaneously.</li>
          </ul>
        ),
      },
      {
        id: 'permissions',
        title: 'Permissions',
        body: (
          <p>Editing requires the <em>Incident.UpdateDetails</em> permission. Typically available to Admins and assigned Incident Commanders.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-roster-views',
    slug: 'roster-views',
    title: 'Roster Views',
    summary: 'Choose how to view the roster: Full Roster, Visitors, by floor, practice, or person type.',
    category: 'Core Workflow',
    audience: ['Headcounter', 'Incident Commander'],
    keywords: ['roster', 'views', 'full roster', 'visitors', 'floor', 'practice', 'person type', 'alpha', 'search'],
    relatedRoutes: ['/incidents/:incidentId/views'],
    relatedTopicSlugs: ['headcount', 'roster-scopes-filters'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Roster View Selection page is the entry point for headcount. It lets you choose how to slice the roster before starting to mark people.</p>
        ),
      },
      {
        id: 'view-options',
        title: 'Available Views',
        body: (
          <ul>
            <li><strong>Full Roster</strong> — All people in the accountability roster.</li>
            <li><strong>Visitors</strong> — Quick access to all visitor, guest, and contractor people.</li>
            <li><strong>By Practice</strong> — Grouped by organizational practice/department.</li>
            <li><strong>By Floor</strong> — Grouped by floor or work area.</li>
            <li><strong>By Person Type</strong> — Grouped by employee, contractor, visitor, tenant, etc.</li>
          </ul>
        ),
      },
      {
        id: 'features',
        title: 'Features',
        body: (
          <ul>
            <li><strong>Alpha range selector</strong> — Filter by last-name ranges (A–F, G–L, etc.).</li>
            <li><strong>Person search jump</strong> — Search for a specific person and jump to their roster view.</li>
            <li><strong>Status filter chips</strong> — Filter views by All, Not Accounted, or Not Checked with live counts.</li>
            <li><strong>Real-time updates</strong> — Counts update as headcounters mark people.</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: 'cw-headcount',
    slug: 'headcount',
    title: 'Headcount',
    summary: 'Mark people as Accounted or Not Accounted with search, filters, and offline sync.',
    category: 'Core Workflow',
    audience: ['Headcounter', 'Incident Commander'],
    keywords: ['headcount', 'mark', 'accounted', 'not accounted', 'search', 'filter', 'sort', 'add person', 'pending', 'sync', 'offline'],
    relatedRoutes: ['/incidents/:incidentId/headcount'],
    relatedTopicSlugs: ['roster-views', 'roster-scopes-filters', 'not-accounted', 'review-submit', 'realtime-sync', 'tenant-records'],
    sections: [
      {
        id: 'marking',
        title: 'Marking People',
        body: (
          <p>Each person row has Accounted and Not Accounted buttons. Tap a button to set the person's status. The row highlights to reflect the change. Changes are queued locally and auto-synced every 5 seconds.</p>
        ),
      },
      {
        id: 'search-filter',
        title: 'Search and Filters',
        body: (
          <ul>
            <li><strong>Search</strong> — Type a name to filter the roster instantly.</li>
            <li><strong>Status chips</strong> — Filter by All, Accounted, Not Accounted, or Not Checked.</li>
            <li><strong>Sort</strong> — Sort by name (A–Z or Z–A) or status.</li>
            <li><strong>Advanced filters</strong> — Open the filter drawer for person type, source context, and tenant company filters.</li>
          </ul>
        ),
      },
      {
        id: 'scope',
        title: 'Roster Scope',
        body: (
          <p>The scope selector lets you switch between Accountability (default — people who badged in), Full Roster (all assigned), and No-Badge (assigned but did not badge in). Counts adjust based on scope.</p>
        ),
      },
      {
        id: 'add-person',
        title: 'Add Unlisted Person',
        body: (
          <p>If someone is physically present but not on the roster, click "Add Person" to add them manually. Enter their name, select a person type, and optionally add notes. This requires the <em>Roster.AddUnlistedPerson</em> permission.</p>
        ),
      },
      {
        id: 'pending-sync',
        title: 'Pending Sync',
        body: (
          <p>Changes are saved locally first and synced to the server in batches every 5 seconds. A sync indicator shows the current state: online, saving, synced, offline, error, or reconnecting. Do not navigate away while changes are pending — a navigation guard dialog will prompt you to save, discard, or stay.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-not-accounted',
    slug: 'not-accounted',
    title: 'Not Accounted Review',
    summary: 'Focused view of people marked Not Accounted or Not Checked, with export and inline actions.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Headcounter'],
    keywords: ['not accounted', 'missing', 'unaccounted', 'needs attention', 'export', 'csv'],
    relatedRoutes: ['/incidents/:incidentId/unaccounted'],
    relatedTopicSlugs: ['headcount', 'incident-dashboard', 'review-submit'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Not Accounted page shows a focused list of people who are still marked as Not Accounted or Not Checked. This is the primary triage view for resolving accountability gaps.</p>
        ),
      },
      {
        id: 'features',
        title: 'Features',
        body: (
          <ul>
            <li><strong>Summary cards</strong> — Shows counts for Not Accounted (red) and Not Checked (gray).</li>
            <li><strong>Filter chips</strong> — Switch between Not Accounted and Not Checked with count badges.</li>
            <li><strong>Search</strong> — Find a specific person by name.</li>
            <li><strong>Inline marking</strong> — Mark a person as Accounted directly from this page.</li>
            <li><strong>Export</strong> — Download the list as a CSV file.</li>
          </ul>
        ),
      },
      {
        id: 'what-not-accounted-means',
        title: 'What "Not Accounted" Means',
        body: (
          <p>A person is "Not Accounted" when a headcounter has explicitly marked them as not found or not present. "Not Checked" means no one has checked on them yet. Both statuses need attention before the incident can be closed cleanly.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-review-submit',
    slug: 'review-submit',
    title: 'Review & Submit / Checkpoint',
    summary: 'Review accountability progress and record a checkpoint without closing the incident.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Headcounter'],
    keywords: ['review', 'submit', 'checkpoint', 'progress', 'snapshot', 'continue'],
    relatedRoutes: ['/incidents/:incidentId/review'],
    relatedTopicSlugs: ['headcount', 'not-accounted', 'close-incident'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Review &amp; Submit page shows a summary of your current accountability progress: Total, Accounted, Not Accounted, and Not Checked counts.</p>
        ),
      },
      {
        id: 'checkpoint',
        title: 'Recording a Checkpoint',
        body: (
          <>
            <p>Clicking "Record Checkpoint &amp; Continue" saves a snapshot of the current state. Important: a checkpoint does <strong>not</strong> close the incident. You can continue updating statuses and record additional checkpoints.</p>
            <p>If there are unresolved people, a confirmation dialog appears asking you to acknowledge before proceeding.</p>
          </>
        ),
      },
      {
        id: 'permissions',
        title: 'Permissions',
        body: (
          <p>Recording a checkpoint is permission-gated. If you do not see the button, you may not have the required role for this incident.</p>
        ),
      },
    ],
  },
  {
    id: 'cw-close-incident',
    slug: 'close-incident',
    title: 'Close Incident',
    summary: 'How to close an incident with acknowledgement, notes, and understanding read-only behavior.',
    category: 'Core Workflow',
    audience: ['Incident Commander', 'Admin'],
    keywords: ['close', 'close incident', 'closure', 'read-only', 'closed', 'acknowledge', 'notes'],
    relatedTopicSlugs: ['incident-dashboard', 'review-submit', 'reports-exports'],
    sections: [
      {
        id: 'steps',
        title: 'Steps to Close',
        body: (
          <ol>
            <li>Open the Incident Dashboard.</li>
            <li>Click <strong>"Close Incident"</strong> in the header.</li>
            <li>Review the closure summary: incident name, timestamp, and accountability progress bar.</li>
            <li>If there are unresolved Not Accounted people, a warning banner shows the count. You can click "Review remaining people before closing" to navigate to the Not Accounted page.</li>
            <li>If there are unsynced changes, a warning is displayed. Wait for sync to complete.</li>
            <li>Check the acknowledgement checkbox to enable the Close button.</li>
            <li>Optionally add closing notes.</li>
            <li>Click <strong>"Close Incident"</strong>.</li>
          </ol>
        ),
      },
      {
        id: 'after-close',
        title: 'After Closing',
        body: (
          <ul>
            <li>A "Closed Incident" banner appears on all pages.</li>
            <li>"View Final Roster" replaces "Start Headcount".</li>
            <li>Status toggles become read-only — no further headcount updates are allowed.</li>
            <li>Reports and CSV exports remain available.</li>
          </ul>
        ),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // REFERENCE
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'ref-scopes-filters',
    slug: 'roster-scopes-filters',
    title: 'Roster Scopes and Filters',
    summary: 'Understand accountability scopes, person type filters, tenant company filters, and roster contexts.',
    category: 'Reference',
    audience: ['Headcounter', 'Incident Commander', 'Admin'],
    keywords: ['scope', 'accountability', 'full roster', 'no badge', 'filter', 'person type', 'roster context', 'tenant company', 'badge', 'clear filters'],
    relatedTopicSlugs: ['headcount', 'roster-views', 'tenant-records'],
    sections: [
      {
        id: 'scopes',
        title: 'Roster Scopes',
        body: (
          <ul>
            <li><strong>Accountability</strong> (default) — People who badged in today. This is the primary scope for accountability calculations.</li>
            <li><strong>Full Roster</strong> — All people assigned to the location, regardless of badge activity.</li>
            <li><strong>No Badge Today</strong> — People assigned to the location who did not badge in. Useful for identifying people who may be working remotely or absent.</li>
          </ul>
        ),
      },
      {
        id: 'filters',
        title: 'Filter Options',
        body: (
          <ul>
            <li><strong>Person Type</strong> — Employee, Contractor, Visitor, Tenant, etc.</li>
            <li><strong>Roster Context</strong> — Badge-in, Lobby, Assigned, Manual.</li>
            <li><strong>Tenant Company</strong> — Filter by tenant company name (e.g., Regus).</li>
            <li><strong>Status</strong> — Accounted, Not Accounted, Not Checked.</li>
          </ul>
        ),
      },
      {
        id: 'clearing',
        title: 'Clearing Filters',
        body: (
          <p>Active filters appear as chips below the toolbar. Click the "X" on any chip to remove it, or use "Clear All" to reset all filters. The "Show X people" count updates in real time.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-tenant-records',
    slug: 'tenant-records',
    title: 'Tenant Records',
    summary: 'How tenant personnel appear in the roster and how to work with tenant data.',
    category: 'Reference',
    audience: ['Headcounter', 'Incident Commander', 'Admin'],
    keywords: ['tenant', 'tenants', 'company', 'regus', 'badge', 'email', 'phone', 'person type'],
    relatedTopicSlugs: ['roster-scopes-filters', 'headcount'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>Tenants are people who work in the building but belong to a different company (e.g., Regus). They appear in the roster with a "Tenant" person type and may have a company name field.</p>
        ),
      },
      {
        id: 'data',
        title: 'Tenant Data',
        body: (
          <ul>
            <li>Tenant rows show the person type as "Tenant" and include a company name (companyName) when available.</li>
            <li>Badge data is included for tenants who use the building access system.</li>
            <li>Email and phone contact information may not be available for all tenants.</li>
          </ul>
        ),
      },
      {
        id: 'counting',
        title: 'Counting Tenants',
        body: (
          <p>Tenant rows can be counted (marked Accounted/Not Accounted) by authorized Exponent users. In V1, tenant users do not log into the app directly.</p>
        ),
      },
      {
        id: 'search',
        title: 'Finding Tenants',
        body: (
          <p>Use the Tenant Company filter in the advanced filter drawer, or search by company name (e.g., "Regus") in the headcount search bar.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-incident-team',
    slug: 'incident-team',
    title: 'Incident Team',
    summary: 'View team assignments, roles, and contact information for the incident.',
    category: 'Reference',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['team', 'incident commander', 'office director', 'headcounter', 'recall', 'ECT', 'zones', 'contact', 'email', 'phone', 'assignments'],
    relatedRoutes: ['/incidents/:incidentId/team'],
    relatedTopicSlugs: ['incident-dashboard'],
    sections: [
      {
        id: 'roles',
        title: 'Team Roles',
        body: (
          <ul>
            <li><strong>Incident Commander</strong> — Leads the evacuation event and has full management access.</li>
            <li><strong>Office Director</strong> — Senior leader responsible for site operations.</li>
            <li><strong>Headcounter</strong> — Responsible for physically locating and marking people.</li>
            <li><strong>Recall Team</strong> — Supports recall and headcount operations.</li>
            <li><strong>ECT Lead, Zone 1, Zone 2, Zone 3, Lab Zone, Accountability Coordinator</strong> — Assign these roles in Settings → Users. They have the same access as Incident Commander, including initiating incidents and commander visibility within their assigned scope. Like Incident Commander, they can manage Users &amp; Roles globally, subject to SystemAdmin safeguards. They appear prominently in incident team availability. Standing-role changes apply to newly created incident teams.</li>
          </ul>
        ),
      },
      {
        id: 'accessing',
        title: 'Accessing the Team',
        body: (
          <ul>
            <li><strong>Team page</strong> — Navigate to Team in the left sidebar within an incident. Shows a full directory grouped by role with search and collapsible sections.</li>
            <li><strong>Team Drawer</strong> — Click the team summary strip on the Dashboard for a quick slide-out panel without leaving the page.</li>
          </ul>
        ),
      },
      {
        id: 'contact',
        title: 'Contact',
        body: (
          <p>Click a phone number to initiate a call, or click an email address to open your email client. Contact links are shown when available.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-facility-map',
    slug: 'facility-map',
    title: 'Facility Map',
    summary: 'View location-specific evacuation floor plan PDFs.',
    category: 'Reference',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['facility', 'map', 'evacuation', 'floor plan', 'pdf', 'download', 'floor', 'tabs'],
    relatedRoutes: ['/incidents/:incidentId/facility-map'],
    relatedTopicSlugs: ['incident-dashboard'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>The Facility Map page shows evacuation floor plan PDFs specific to the incident location. Maps are available for 20 locations.</p>
        ),
      },
      {
        id: 'features',
        title: 'Features',
        body: (
          <ul>
            <li><strong>Floor tabs</strong> — For multi-floor sites, use tabs to switch between different floor maps.</li>
            <li><strong>Open in New Tab</strong> — View the PDF full-screen in a new browser tab.</li>
            <li><strong>Download</strong> — Save the PDF to your device.</li>
          </ul>
        ),
      },
      {
        id: 'note',
        title: 'Important',
        body: (
          <p>The facility map is informational only. It does not affect headcount data or accountability status.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-reports-exports',
    slug: 'reports-exports',
    title: 'Reports and Exports',
    summary: 'Browse incident reports and export data as CSV files.',
    category: 'Reference',
    audience: ['Incident Commander', 'Admin', 'Viewer'],
    keywords: ['reports', 'export', 'csv', 'download', 'active', 'closed', 'incident report', 'headcount report'],
    relatedRoutes: ['/reports'],
    relatedTopicSlugs: ['incident-dashboard', 'not-accounted'],
    sections: [
      {
        id: 'reports-page',
        title: 'Reports Page',
        body: (
          <p>Navigate to Reports in the left sidebar. Use status filter chips (Active, Closed, All) and search to find incidents. Click an incident to view its report with summary metrics.</p>
        ),
      },
      {
        id: 'export-options',
        title: 'Export Options',
        body: (
          <ul>
            <li><strong>Export Headcount Report</strong> — Full roster CSV, available from Dashboard Quick Actions.</li>
            <li><strong>Export Report</strong> — Summary CSV, available from the Dashboard header button.</li>
            <li><strong>Export Not Accounted List</strong> — CSV of unaccounted/not-checked people, available from the Not Accounted page.</li>
          </ul>
        ),
      },
      {
        id: 'closed-reports',
        title: 'Closed Incident Reports',
        body: (
          <p>Reports and exports remain fully available for closed incidents. The data reflects the final state at the time of closure.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-realtime-sync',
    slug: 'realtime-sync',
    title: 'Real-Time Updates and Offline Sync',
    summary: 'How live updates, offline tolerance, pending sync, and navigation guards work.',
    category: 'Reference',
    audience: ['Headcounter', 'Incident Commander', 'Admin'],
    keywords: ['realtime', 'real-time', 'live', 'signalr', 'websocket', 'offline', 'sync', 'pending', 'reconnecting', 'navigation guard', 'online'],
    relatedTopicSlugs: ['headcount', 'incident-dashboard', 'troubleshooting-sync'],
    sections: [
      {
        id: 'live-updates',
        title: 'Live Updates',
        body: (
          <ul>
            <li>EEAS uses SignalR (WebSocket) for real-time updates. When another headcounter marks someone, your page updates automatically.</li>
            <li>A green "Live" indicator appears on pages with an active real-time connection.</li>
            <li>"Last Updated" timestamps show when data was last refreshed.</li>
          </ul>
        ),
      },
      {
        id: 'adaptive-polling',
        title: 'Adaptive Polling',
        body: (
          <p>When the real-time connection is active, polling slows down (reconciliation only). When disconnected, polling speeds up as a fallback. Headcount refreshes every 5 seconds without real-time, Dashboard every 30 seconds.</p>
        ),
      },
      {
        id: 'pending-sync',
        title: 'Pending Sync',
        body: (
          <p>Status changes are saved locally first and synced to the server every 5 seconds. The sync indicator shows the current state: online, saving, synced, offline, error, or reconnecting.</p>
        ),
      },
      {
        id: 'offline',
        title: 'Going Offline',
        body: (
          <ul>
            <li>If you lose connectivity, changes are preserved locally and will sync when you reconnect.</li>
            <li>Keep the page open — do not close the browser tab until sync completes.</li>
            <li>A navigation guard dialog prevents accidental loss of unsynced changes.</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: 'ref-roles-permissions',
    slug: 'roles-permissions',
    title: 'Roles and Permissions',
    summary: 'Understand why buttons may be hidden and what permissions control each action.',
    category: 'Reference',
    audience: ['Incident Commander', 'Admin', 'Viewer'],
    keywords: ['roles', 'permissions', 'access', 'denied', 'hidden', 'button', 'create', 'edit', 'close', 'admin', 'location'],
    relatedTopicSlugs: ['getting-started', 'troubleshooting-permissions'],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p>EEAS uses role-based permissions to control which actions are available to each user. If a button or action is missing, it usually means your account does not have the required permission.</p>
        ),
      },
      {
        id: 'permissions-list',
        title: 'Permission Reference',
        body: (
          <ul>
            <li><strong>Incident.Create</strong> — Create new incidents. Controls the "New Incident" button.</li>
            <li><strong>Roster.UpdateStatus</strong> — Mark people as Accounted or Not Accounted.</li>
            <li><strong>Roster.AddUnlistedPerson</strong> — Add unlisted people to the roster.</li>
            <li><strong>Incident.ViewUnaccounted</strong> — View the Not Accounted page.</li>
            <li><strong>Reports.Export</strong> — Export CSV reports.</li>
            <li><strong>Incident.UpdateDetails</strong> — Edit incident type, name, and notes.</li>
          </ul>
        ),
      },
      {
        id: 'location-scope',
        title: 'Location-Scoped Access',
        body: (
          <p>Some permissions are scoped to specific locations. You may have full access at your home office but limited access at other locations. Contact your administrator if your access seems incorrect.</p>
        ),
      },
    ],
  },
  {
    id: 'ref-glossary',
    slug: 'glossary',
    title: 'Glossary',
    summary: 'Definitions of key EEAS terms and concepts.',
    category: 'Reference',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['glossary', 'terms', 'definitions', 'vocabulary', 'meaning'],
    relatedTopicSlugs: ['getting-started', 'roster-scopes-filters', 'incident-team'],
    sections: [
      {
        id: 'terms',
        title: 'Terms',
        body: (
          <dl>
            <dt><strong>Accounted</strong></dt>
            <dd>A person has been physically located and confirmed present at the evacuation assembly point.</dd>

            <dt><strong>Not Accounted</strong></dt>
            <dd>A headcounter has explicitly marked a person as not found or not present. Requires follow-up.</dd>

            <dt><strong>Not Checked</strong></dt>
            <dd>No headcounter has checked on this person yet. Default status at incident start.</dd>

            <dt><strong>Accountability Roster</strong></dt>
            <dd>The default roster scope showing only people who badged into the building today.</dd>

            <dt><strong>Full Roster</strong></dt>
            <dd>All people assigned to the location, regardless of whether they badged in.</dd>

            <dt><strong>No Badge Today</strong></dt>
            <dd>People assigned to the location who did not badge in today. May indicate remote workers or absentees.</dd>

            <dt><strong>Incident Team</strong></dt>
            <dd>The group of people assigned to manage the incident: Commanders, Directors, ECT role members, Headcounters, and Recall Team members.</dd>

            <dt><strong>Incident Commander</strong></dt>
            <dd>The lead person responsible for managing the evacuation event from creation to closure.</dd>

            <dt><strong>Headcounter</strong></dt>
            <dd>A person assigned to physically locate people and mark their accountability status.</dd>

            <dt><strong>Recall Team</strong></dt>
            <dd>A secondary headcounter who steps in if the primary is unavailable.</dd>

            <dt><strong>Office Director</strong></dt>
            <dd>A senior leader responsible for site operations during an evacuation.</dd>

            <dt><strong>Tenant</strong></dt>
            <dd>A person who works in the building but belongs to a different company (e.g., Regus).</dd>

            <dt><strong>Roster Context</strong></dt>
            <dd>The source that placed a person on the roster: Badge-in, Lobby sign-in, Assigned, or Manually added.</dd>

            <dt><strong>Checkpoint</strong></dt>
            <dd>A saved snapshot of accountability progress. Does not close the incident — headcount can continue.</dd>

            <dt><strong>Closed Incident</strong></dt>
            <dd>An incident that has been formally closed. Headcount status cannot be changed, but reports remain available.</dd>

            <dt><strong>Pending Sync</strong></dt>
            <dd>Status changes saved locally that have not yet been sent to the server. Indicated by a sync status badge.</dd>
          </dl>
        ),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // TROUBLESHOOTING
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'ts-permissions',
    slug: 'troubleshooting-permissions',
    title: "Why Can't I See a Button?",
    summary: 'Troubleshooting missing buttons and actions due to permissions.',
    category: 'Troubleshooting',
    audience: ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'],
    keywords: ['button', 'missing', 'hidden', 'disabled', 'access denied', 'permissions', 'cannot see', 'why'],
    relatedTopicSlugs: ['roles-permissions'],
    sections: [
      {
        id: 'common-causes',
        title: 'Common Causes',
        body: (
          <ul>
            <li><strong>Missing permission</strong> — Each action (create incident, mark status, add person, export, close) requires a specific permission. See the Roles and Permissions topic for the full list.</li>
            <li><strong>Location scope</strong> — You may have permissions at one location but not another. The button may appear at your home office but not at other sites.</li>
            <li><strong>Closed incident</strong> — Headcount actions are disabled for closed incidents. Only reporting and export actions remain available.</li>
          </ul>
        ),
      },
      {
        id: 'resolution',
        title: 'What to Do',
        body: (
          <p>If you believe your access is incorrect, contact your EEAS administrator to review your role assignments and location permissions.</p>
        ),
      },
    ],
  },
  {
    id: 'ts-sync',
    slug: 'troubleshooting-sync',
    title: 'Pending Sync / Offline',
    summary: 'Troubleshooting sync issues, offline behavior, and unsynced changes.',
    category: 'Troubleshooting',
    audience: ['Headcounter', 'Incident Commander'],
    keywords: ['sync', 'offline', 'pending', 'unsynced', 'reconnecting', 'error', 'lost connection', 'not saving'],
    relatedTopicSlugs: ['realtime-sync', 'headcount'],
    sections: [
      {
        id: 'symptoms',
        title: 'Symptoms',
        body: (
          <ul>
            <li>Sync indicator shows "Offline", "Error", or "Reconnecting".</li>
            <li>The Unsynced count on the Dashboard is not decreasing.</li>
            <li>Changes are not reflected when you refresh the page.</li>
          </ul>
        ),
      },
      {
        id: 'steps',
        title: 'Resolution Steps',
        body: (
          <ol>
            <li>Do not close the browser tab or navigate away — your changes are preserved locally.</li>
            <li>Check your network connection (Wi-Fi, cellular, ethernet).</li>
            <li>Wait for the automatic reconnection. The app retries with increasing backoff intervals.</li>
            <li>Once "Online" or "Synced" appears, your changes have been saved.</li>
            <li>If the issue persists, try refreshing the page. Note: unsynced changes will be lost on refresh if they have not been sent to the server yet.</li>
          </ol>
        ),
      },
    ],
  },
  {
    id: 'ts-not-accounted-remain',
    slug: 'troubleshooting-not-accounted',
    title: 'Not Accounted People Remain',
    summary: 'Why some people remain Not Accounted and how to resolve them.',
    category: 'Troubleshooting',
    audience: ['Incident Commander', 'Headcounter'],
    keywords: ['not accounted', 'remain', 'stuck', 'still showing', 'unresolved', 'missing people'],
    relatedTopicSlugs: ['not-accounted', 'headcount', 'close-incident'],
    sections: [
      {
        id: 'reasons',
        title: 'Common Reasons',
        body: (
          <ul>
            <li><strong>Not yet checked</strong> — The person may still be in "Not Checked" status, meaning no headcounter has evaluated them.</li>
            <li><strong>Pending sync</strong> — The Accounted status may have been set locally but not yet synced. Check the sync indicator.</li>
            <li><strong>Different scope</strong> — You may be viewing a different roster scope. Switch to Accountability or Full Roster to see all people.</li>
            <li><strong>Genuinely missing</strong> — The person has not been physically located.</li>
          </ul>
        ),
      },
      {
        id: 'resolution',
        title: 'Resolution',
        body: (
          <p>Go to the Not Accounted page and review each person. If they are now located, mark them as Accounted. If they are genuinely missing, leave them as Not Accounted — the Close Incident dialog will show the count of unresolved people.</p>
        ),
      },
    ],
  },
  {
    id: 'ts-export',
    slug: 'troubleshooting-export',
    title: 'Export Issues',
    summary: 'Troubleshooting CSV export problems.',
    category: 'Troubleshooting',
    audience: ['Incident Commander', 'Admin', 'Viewer'],
    keywords: ['export', 'csv', 'download', 'not working', 'empty', 'missing data'],
    relatedTopicSlugs: ['reports-exports', 'incident-dashboard'],
    sections: [
      {
        id: 'common-issues',
        title: 'Common Issues',
        body: (
          <ul>
            <li><strong>Empty export</strong> — If no data matches the current filters, the CSV may be empty. Clear filters and try again.</li>
            <li><strong>Download blocked</strong> — Your browser may block downloads. Check the download bar or popup blocker settings.</li>
            <li><strong>Permission required</strong> — Export actions require the <em>Reports.Export</em> permission.</li>
            <li><strong>Pending sync</strong> — If headcount changes have not synced yet, the export may not include the latest data. Wait for sync to complete before exporting.</li>
          </ul>
        ),
      },
    ],
  },
];
